import { useMemo } from "react";
import {
    QueryClient,
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";

import { MeditationsAPI } from "../infrastructure/api/Meditations.api";
import {
    MEDITATIONS_PAGE_SIZE,
    Meditation,
    MeditationDraft,
    MeditationFilters,
    MeditationLicense,
    MeditationLikeState,
} from "../domain/types";

const OWN_KEY = "meditations";
const PUBLIC_KEY = "meditations-public";
const CENTER_KEY = "meditations-center";
const RECIPIENTS_KEY = "meditation-recipients";

/** The libraries move together: publishing shows up in one and the other. */
const invalidate = (queryClient: ReturnType<typeof useQueryClient>) => {
    queryClient.invalidateQueries({ queryKey: [OWN_KEY] });
    queryClient.invalidateQueries({ queryKey: [PUBLIC_KEY] });
    queryClient.invalidateQueries({ queryKey: [CENTER_KEY] });
};

export const useMeditations = (filters: MeditationFilters = {}, enabled = true) =>
    useQuery({
        queryKey: [OWN_KEY, filters],
        queryFn: async () => {
            const { data } = await MeditationsAPI.list(filters);
            return data;
        },
        enabled,
    });

/**
 * One page of the public library, numbered from 1. It grows with every
 * therapist who publishes, so it is read from the server a page at a time.
 *
 * One more than a page is asked for, to know whether there is a next page
 * without counting the whole library; `data` is the page itself, `hasNext`
 * says whether another follows.
 */
export const usePublicMeditations = (
    filters: MeditationFilters = {},
    page = 1,
    enabled = true
) => {
    const query = useQuery({
        queryKey: [PUBLIC_KEY, filters, page],
        queryFn: async () => {
            const { data } = await MeditationsAPI.listPublic(filters, {
                offset: (page - 1) * MEDITATIONS_PAGE_SIZE,
                limit: MEDITATIONS_PAGE_SIZE + 1,
            });
            return data;
        },
        // The page being left stays on screen until the next one arrives.
        placeholderData: keepPreviousData,
        enabled,
    });
    const data = useMemo(() => query.data?.slice(0, MEDITATIONS_PAGE_SIZE), [query.data]);
    return { ...query, data, hasNext: (query.data?.length ?? 0) > MEDITATIONS_PAGE_SIZE };
};

/** The library of the therapist's centre. Empty for one who is in none. */
export const useCenterMeditations = (filters: MeditationFilters = {}, enabled = true) =>
    useQuery({
        queryKey: [CENTER_KEY, filters],
        queryFn: async () => {
            const { data } = await MeditationsAPI.listCenter(filters);
            return data;
        },
        enabled,
    });

/**
 * Everything a therapist can take into a session: their own meditations, then
 * their centre's.
 */
export const useAvailableMeditations = () => {
    const own = useMeditations();
    const center = useCenterMeditations();
    return {
        data: [...(own.data ?? []), ...(center.data ?? [])],
        isLoading: own.isLoading || center.isLoading,
    };
};

export const useMeditation = (id?: string) =>
    useQuery({
        queryKey: [OWN_KEY, "detail", id],
        queryFn: async () => {
            const { data } = await MeditationsAPI.get(id as string);
            return data;
        },
        enabled: Boolean(id),
    });

export const useCreateMeditation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (draft: MeditationDraft) => {
            const { data } = await MeditationsAPI.create(draft);
            return data;
        },
        onSuccess: () => invalidate(queryClient),
    });
};

export const useUpdateMeditation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            changes,
        }: {
            id: string;
            changes: Partial<MeditationDraft>;
        }) => {
            const { data } = await MeditationsAPI.update(id, changes);
            return data;
        },
        onSuccess: () => invalidate(queryClient),
    });
};

export const useDeleteMeditation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => MeditationsAPI.remove(id),
        onSuccess: () => invalidate(queryClient),
    });
};

export const usePublishMeditation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            authorName,
            license,
        }: {
            id: string;
            authorName?: string | null;
            license?: MeditationLicense;
        }) => {
            const { data } = await MeditationsAPI.publish(id, {
                author_name: authorName,
                license,
            });
            return data;
        },
        onSuccess: () => invalidate(queryClient),
    });
};

export const useUnpublishMeditation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { data } = await MeditationsAPI.unpublish(id);
            return data;
        },
        onSuccess: () => invalidate(queryClient),
    });
};

export const useCopyMeditation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { data } = await MeditationsAPI.copy(id);
            return data;
        },
        onSuccess: () => invalidate(queryClient),
    });
};

/** Put a heart's new state on the meditation wherever a library lists it. */
const setLike = (queryClient: QueryClient, id: string, state: MeditationLikeState) => {
    for (const key of [OWN_KEY, PUBLIC_KEY, CENTER_KEY]) {
        const update = (list: Meditation[]) =>
            list.map((m) => (m.id === id ? { ...m, ...state } : m));
        // A list, or the one meditation its own page shows.
        queryClient.setQueriesData<Meditation[] | Meditation>({ queryKey: [key] }, (cached) =>
            Array.isArray(cached)
                ? update(cached)
                : cached && cached.id === id
                  ? { ...cached, ...state }
                  : cached
        );
    }
};

/**
 * Like a meditation, or take the like back.
 *
 * The heart moves at once and the count with it; the server's answer then
 * settles the count, since others may have liked it in the meantime. A failure
 * reloads the libraries rather than leaving a heart that was never recorded.
 */
export const useToggleMeditationLike = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (meditation: Meditation) => {
            const { data } = meditation.liked_by_me
                ? await MeditationsAPI.unlike(meditation.id)
                : await MeditationsAPI.like(meditation.id);
            return data;
        },
        onMutate: async (meditation) => {
            // A refetch landing after this would put the old heart back.
            await Promise.all(
                [OWN_KEY, PUBLIC_KEY, CENTER_KEY].map((key) =>
                    queryClient.cancelQueries({ queryKey: [key] })
                )
            );
            const liked = !meditation.liked_by_me;
            const count = Math.max(0, (meditation.like_count ?? 0) + (liked ? 1 : -1));
            setLike(queryClient, meditation.id, { like_count: count, liked_by_me: liked });
        },
        onSuccess: (state, meditation) => setLike(queryClient, meditation.id, state),
        onError: () => invalidate(queryClient),
    });
};

/**
 * Who this meditation was given to.
 *
 * Its own key rather than part of the meditation: a therapist opens the share
 * sheet on one meditation at a time, and the library does not need to know.
 */
export const useMeditationRecipients = (id?: string) =>
    useQuery({
        queryKey: [RECIPIENTS_KEY, id],
        queryFn: async () => {
            const { data } = await MeditationsAPI.recipients(id as string);
            return data;
        },
        enabled: Boolean(id),
    });

export const useShareMeditation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            id,
            patientId,
        }: {
            id: string;
            patientId: string;
        }) => MeditationsAPI.share(id, patientId),
        onSuccess: (_data, { id }) =>
            queryClient.invalidateQueries({ queryKey: [RECIPIENTS_KEY, id] }),
    });
};

export const useUnshareMeditation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            id,
            patientId,
        }: {
            id: string;
            patientId: string;
        }) => MeditationsAPI.unshare(id, patientId),
        onSuccess: (_data, { id }) =>
            queryClient.invalidateQueries({ queryKey: [RECIPIENTS_KEY, id] }),
    });
};
